// Client for the "Continue with X" OAuth handshake (see
// infra/lambda/twitter-auth/index.mjs for the server side and
// docs/operations/x-login-setup.md for the setup/runbook). Unlike Google, this
// does NOT go through Amplify's signInWithRedirect/Cognito hosted UI at all -
// X isn't a natively-supported Cognito identity provider, so this app's own
// backend performs the whole OAuth 2.0 + PKCE exchange with X directly, then
// this file finishes the sign-in locally by answering a Cognito CUSTOM_AUTH
// challenge with the one-time ticket the backend hands back.
import { signIn, confirmSignIn, signOut } from "aws-amplify/auth";

const PUBLIC_API_URL = "https://d29a52ery9.execute-api.us-east-1.amazonaws.com/prod";
const TWITTER_AUTH_URL = `${PUBLIC_API_URL}/auth/twitter`;

/** The exact page the OAuth round-trip lands back on once X redirects to our backend. */
export const TWITTER_FINISH_PATH = "/auth/twitter/finish";

/**
 * Build-time switch for the "Continue with X" button (Login and Register) and
 * the "Connect X" card (Profile). Off unless the build sets
 * REACT_APP_TWITTER_LOGIN_ENABLED=true, which should happen only once the
 * backend is deployed and verified (docs/operations/x-login-setup.md): the
 * button is useless - and the route it calls does not exist - before then.
 * A function, not a constant, so tests can flip it per case.
 */
export function isTwitterLoginEnabled(): boolean {
    return process.env.REACT_APP_TWITTER_LOGIN_ENABLED === "true";
}

export type TwitterAuthLanguage = "en" | "pt";

// The OAuth round-trip leaves the site and comes back on /auth/twitter/finish,
// which has no /pt twin, so the language the learner started in would be lost
// and a Portuguese visitor would meet English errors. sessionStorage survives
// a same-tab round-trip through another site; it is a UI preference, not data
// about the person, and everything still works (in English) without it.
const LANGUAGE_STORAGE_KEY = "nihongotech.twitterAuthLanguage";

export function rememberTwitterAuthLanguage(language: string | undefined): void {
    try {
        sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language === "pt" ? "pt" : "en");
    } catch {
        /* storage unavailable (private mode, blocked): fall back to English */
    }
}

export function readTwitterAuthLanguage(): TwitterAuthLanguage {
    try {
        return sessionStorage.getItem(LANGUAGE_STORAGE_KEY) === "pt" ? "pt" : "en";
    } catch {
        return "en";
    }
}

/**
 * Starts "Continue with X" from the logged-out Login/Register page: a
 * plain top-level navigation, since there's no bearer token to carry yet.
 */
export function startTwitterSignIn(language?: string): void {
    rememberTwitterAuthLanguage(language);
    const params = new URLSearchParams({ returnOrigin: window.location.origin });
    window.location.assign(`${TWITTER_AUTH_URL}/start?${params.toString()}`);
}

/**
 * Thrown when "Connect X" cannot start. `code` is the backend's stable code
 * (currently only "federated_account": the account signs in through Google/
 * Facebook, which X cannot be added to), when it supplied one.
 */
export class TwitterLinkError extends Error {
    code?: string;

    constructor(message: string, code?: string) {
        super(message);
        this.name = "TwitterLinkError";
        this.code = code;
    }
}

/**
 * Starts "Connect X" from an already signed-in Profile page. Deliberately a
 * fetch() carrying the caller's own ID token in an Authorization header
 * (like every other authenticated call in this app), NOT a top-level
 * navigation with the token in the URL - the backend returns the X
 * authorize URL as plain JSON, and this function performs the actual
 * navigation itself once it has it.
 */
export async function startTwitterLink(idToken: string, language?: string): Promise<void> {
    const params = new URLSearchParams({ returnOrigin: window.location.origin });
    const res = await fetch(`${TWITTER_AUTH_URL}/link/start?${params.toString()}`, {
        headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new TwitterLinkError(
            `Couldn't start connecting X (${res.status})`,
            typeof body?.code === "string" ? body.code : undefined
        );
    }
    const data = await res.json();
    if (typeof data.authorizeUrl !== "string") throw new TwitterLinkError("Malformed response starting X connection");
    rememberTwitterAuthLanguage(language);
    window.location.assign(data.authorizeUrl);
}

export interface TwitterCompleteSignupResult {
    username: string;
    ticket: string;
}

/**
 * Thrown by completeTwitterSignup. Carries the backend's stable `code`
 * (e.g. "existing_account", "already_linked", "expired", "invalid_email")
 * alongside the human-readable `message`, so callers can map distinct
 * failure reasons to distinct, accurate copy instead of guessing from
 * prose text.
 */
export class TwitterCompleteSignupError extends Error {
    code?: string;

    constructor(message: string, code?: string) {
        super(message);
        this.name = "TwitterCompleteSignupError";
        this.code = code;
    }
}

/**
 * Finishes creating a brand-new account for a first-time X sign-in that had
 * no existing NihongoTech account to link to - X never supplies an email,
 * so this is the one place this app ever asks a learner for one on this
 * path. pendingToken comes from the #pendingToken= the callback redirected
 * the browser to /auth/twitter/finish with (the fragment, not the query
 * string: it is a bearer secret just as powerful as the sign-in ticket, so
 * it must never travel to a server or appear in a Referer header).
 */
export async function completeTwitterSignup(
    pendingToken: string,
    email: string
): Promise<TwitterCompleteSignupResult> {
    const res = await fetch(`${TWITTER_AUTH_URL}/complete-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pendingToken, email }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new TwitterCompleteSignupError(
            typeof body.error === "string" ? body.error : `Couldn't finish creating your account (${res.status})`,
            typeof body.code === "string" ? body.code : undefined
        );
    }
    const data = await res.json();
    if (typeof data.username !== "string" || typeof data.ticket !== "string") {
        throw new TwitterCompleteSignupError("Malformed complete-signup response");
    }
    return { username: data.username, ticket: data.ticket };
}

async function startCustomSignIn(username: string) {
    return signIn({ username, options: { authFlowType: "CUSTOM_WITHOUT_SRP" } });
}

/**
 * Answers the Cognito CUSTOM_AUTH challenge with the one-time ticket minted
 * by twitter-auth/index.mjs, turning it into a real signed-in Amplify
 * session - from this point on, fetchAuthSession()/the Identity Pool
 * exchange work completely unmodified, exactly as they do for an
 * email/password or Google sign-in. The Cognito user is the one the backend
 * resolved from the X account's link (or just created), so the learner lands
 * in the same profile, with the same saved progress, every time.
 */
export async function completeTwitterSignIn(username: string, ticket: string): Promise<void> {
    let result;
    try {
        result = await startCustomSignIn(username);
    } catch (err) {
        if ((err as Error)?.name !== "UserAlreadyAuthenticatedException") throw err;
        // Amplify still holds an earlier local session (an old tab, or an
        // interrupted sign-in) - clear it locally and retry once, exactly as
        // the email/password and Google paths in AuthContext do.
        try {
            await signOut();
        } catch {
            /* best-effort local cleanup before retrying */
        }
        result = await startCustomSignIn(username);
    }
    if (result.nextStep?.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
        throw new Error(`Unexpected X sign-in step: ${result.nextStep?.signInStep}`);
    }
    const confirmed = await confirmSignIn({ challengeResponse: ticket });
    if (!confirmed.isSignedIn) {
        throw new Error("X sign-in did not complete");
    }
}

export type TwitterErrorKind =
    | "denied"
    | "alreadyLinked"
    | "sessionExpired"
    | "rateLimited"
    | "unavailable"
    | "generic";

/**
 * Groups the `?error=` codes the backend (or X itself) redirects to
 * /auth/twitter/finish with into the few situations a learner can act on.
 * Unknown codes are "generic" - the raw code is never shown to anyone.
 */
export function classifyTwitterError(code: string | null | undefined): TwitterErrorKind {
    switch (code) {
        case "access_denied":
            return "denied";
        case "twitter_already_linked":
            return "alreadyLinked";
        case "session_expired":
            return "sessionExpired";
        case "x_rate_limited":
            return "rateLimited";
        case "x_unavailable":
        case "server_error":
        case "temporarily_unavailable":
            return "unavailable";
        default:
            return "generic";
    }
}

// Same list as the backend's FEDERATED_USERNAME_PATTERN (infra/lambda/twitter-auth).
const FEDERATED_USERNAME_PATTERN = /^(Google|Facebook|LoginWithAmazon|SignInWithApple)_/;

/**
 * True for an account created through Google/Facebook sign-in. X can't be
 * connected to one (Cognito can't sign a federated user in through the
 * custom-auth flow X uses), so the Profile page doesn't offer it.
 */
export function isFederatedUsername(username: string | null | undefined): boolean {
    return typeof username === "string" && FEDERATED_USERNAME_PATTERN.test(username);
}
